import AnnualTrend from "../AnnualTrend.js";
import BalanceSheetSection from "./section/BalanceSheetSection.js";
import ChartOfAccounts from "../../chartOfAccounts/ChartOfAccounts.js";
import Checklist from "./checklist/Checklist.js";
import FiscalYear from "../../period/FiscalYear.js";
import IncomeStatementSection from "./section/IncomeStatementSection.js";
import KeyFigure from "../../keyFigure/KeyFigure.js";
import KeyFigureResult from "../../keyFigure/KeyFigureResult.js";
import Model from "../../Model.js";
import PeriodReportAttachment from "./PeriodReportAttachment.js";
import PeriodReportEventLog from "./event/PeriodReportEventLog.js";
import periodReportSectionFactory from "./section/periodReportSectionFactory.js";
import UserReference from "../../UserReference.js";

/**
 * `periodReport` represent a period report.
 *
 * @param {period} spec.period - Period covered by the report.
 * @param {UserReference} spec.author - Report author.
 * @param {periodReportSection[]} [spec.sections] - Array of the sections of the
 * period report. When not specified (undefined or empty array), the instance is
 * initialized with default sections (see `this._defaultSections`) for backward
 * compatibility.
 * @param {company} spec.company - Company related to the report data.
 * @param {string} spec.title - Report title.
 * @param {string} spec.subtitle - Report subtitle.
 * @param {string} spec.id - Unique identifier of the report.
 * @param {string} spec.cultureCode - MS culture code (format 'se-SV') used to
 * format numbers.
 * @param {string} spec.comment - Comment let by the author of the report.
 * @param {KeyFigureResult} spec.keyFigureResult - KeyFigure result for the
 * report.
 * @param {report} spec.incomeStatement - Income statement report of the period
 * report.
 * @param {report} spec.balanceSheet - Income statement report of the period
 * report.
 * @param {periodReportEventLog} spec.eventLog - The event log this describes
 * the state of the report.
 * @param {boolean} spec.haveFiscalYearChanged - True if the fiscal year have
 * been updated.
 * @param {annualTrend} spec.overviewData - AnnualTrend data of the period
 * report.
 * @param {periodReportAttachment[]} spec.attachments - Attached files, contains
 * all generated and uploaded attachments.
 * @param {periodReportAttachment[]} spec.generatedAttachments - Files that are
 * generated from server-side.
 * @param {periodReportAttachment[]} spec.uploadedAttachments - Files uploaded
 * by the period report creator
 * @param {Checklist} spec.checklist - The list of todo items this the
 * accountant must check before publishing the receiver.
 * @param {boolean} [spec.wantsAgedInvoices] - In case `spec.sections` is falsy,
 * it indicates if the default sections should include the aged accounts
 * summary section.
 */
export default class PeriodReport extends Model {
	constructor(spec = {}) {
		super(spec);
		this._period = spec.period;
		this._eventLog = spec.eventLog || new PeriodReportEventLog({});
		this._haveFiscalYearChanged = spec.haveFiscalYearChanged;
		this._author = spec.author || "";
		this._company = spec.company;
		this._title = spec.title || "";
		this._subtitle = spec.subtitle || "";
		this._id = spec.id;
		this._cultureCode = spec.cultureCode || "sv-SE";
		this._comment = spec.comment || "";
		this._attachments = spec.attachments || [];

		// List of generated attachments (for old period reports). See the
		// documentation of `periodReportAttachment` for more information about
		// server-side generated attachments.
		this._generatedAttachments = spec.generatedAttachments || [];

		// List of attachments uploaded from the client to the server. See the
		// documentation of `periodReportAttachment` for more information about
		// uploaded attachments.
		this._uploadedAttachments = spec.uploadedAttachments || [];

		this._keyFigureResult = spec.keyFigureResult;
		this._incomeStatement = spec.incomeStatement;
		this._overviewData = spec.overviewData;
		this._balanceSheet = spec.balanceSheet;
		this._wantsAgedInvoices = spec.wantsAgedInvoices;
		this._setSections(spec.sections);

		this._checklist = spec.checklist;
	}

	accept(visitor) {
		return this.isDraft()
			? visitor.visitPeriodReportDraft(this)
			: visitor.visitPeriodReport(this);
	}

	getKeyFigureResult() {
		return this._keyFigureResult;
	}

	getAttachments() {
		return this._attachments;
	}

	getAuthor() {
		return this._author;
	}

	getCompany() {
		return this._company;
	}

	getTitle() {
		return this._title;
	}

	getSubtitle() {
		return this._subtitle;
	}

	getComment() {
		return this._comment;
	}

	getId() {
		return this._id;
	}

	getOverviewData() {
		return this._overviewData;
	}

	getBalanceSheet() {
		return this.findSectionWithId(BalanceSheetSection.getId()).getReport();
	}

	getIncomeStatement() {
		return this.findSectionWithId(
			IncomeStatementSection.getId(),
		).getReport();
	}

	getPeriod() {
		return this._period;
	}

	getSections() {
		return this._sections;
	}

	getChecklist() {
		return this._checklist;
	}

	findSectionWithId(sectionId) {
		return this._sections.find((section) => section.getId() === sectionId);
	}

	getEventLog() {
		return this._eventLog;
	}

	getLastUpdateDate() {
		return this._eventLog.getLastUpdateDate();
	}

	getPublishDate() {
		return this._eventLog.getPublishDate();
	}

	getKeyFigureValues() {
		return this.getKeyFigureResult().getValues();
	}

	isWithdrawn() {
		return this._eventLog.isWithdrawn();
	}

	isWithdrawable() {
		return this._eventLog.isWithdrawable();
	}

	isPublished() {
		return this._eventLog.isPublished();
	}

	isDraft() {
		return this._eventLog.isDraft();
	}

	haveFiscalYearChanged() {
		return this._haveFiscalYearChanged;
	}

	isNull() {
		return false;
	}

	getKeyFigures() {
		return this._keyFigureResult.getKeyFigures();
	}

	isMonthReport() {
		return this._period.isMonth();
	}

	isBimesterReport() {
		return this._period.isBimester();
	}

	isQuarterReport() {
		return this._period.isQuarter();
	}

	isHalfYearReport() {
		return this._period.isHalfYear();
	}

	isYearReport() {
		return this._period.isYear();
	}

	isConsecutiveMonthsReport() {
		return this._period.isConsecutiveMonths();
	}

	hasComment() {
		return !!this._comment;
	}

	hasNonEmptyChecklist() {
		return this._checklist && this._checklist.getItems().length > 0;
	}

	hasBeenAcknowledgedBy(user) {
		return this._eventLog.hasBeenAcknowledgedBy(user);
	}

	defaultAttachments() {
		return this._uploadedAttachments.filter((attachment) =>
			attachment.isDefaultAttachment(),
		);
	}

	shouldFormatInThousands() {
		return [this.getIncomeStatement(), this.getBalanceSheet()].find(
			(report) => report && report.shouldFormatInThousands(),
		);
	}

	/**
	 * Since we are using RPC like resources for the report, all we need to
	 * send back to the backend is the `id` value.
	 */
	serialize() {
		return {
			id: this._id,
		};
	}

	_setSections(sections) {
		if (!sections || sections.length === 0) {
			sections = this._defaultSections();
		}

		this._sections = sections;
		sections.forEach((section) => section.setPeriodReport(this));
	}

	_defaultSections() {
		return periodReportSectionFactory.defaultSections(
			this._wantsAgedInvoices,
		);
	}

	static transformMaterializationData(data, context) {
		const period = context.findPeriodById(data.periodString);
		data.period = period;
		data.eventLog = PeriodReportEventLog.materialize(data.eventLog);
		data.author = UserReference.materialize(data.author);
		data.company = context.getCompany();

		data.attachments = PeriodReportAttachment.materializeAll(
			data.attachments,
			context,
		);

		data.uploadedAttachments = PeriodReportAttachment.materializeAll(
			data.uploadedAttachments,
			context,
		);

		data.generatedAttachments = PeriodReportAttachment.materializeAll(
			data.generatedAttachments,
			context,
		);

		const customKeyFigures = KeyFigure.materializeAll(
			data.customKeyFigures || [],
			context,
		);

		data.keyFigureResult = KeyFigureResult.materialize(
			{
				values: data.keyFigureValues,
				keyFigureIds: data.keyFigureIds,
				keyFigures: customKeyFigures,
				periodIds: [data.periodString],
				periodType: period.getPeriodType(),
			},
			context,
		);

		data.sections = periodReportSectionFactory.materializeAll(
			data.sections || [],
			context,
		);

		data.overviewData = new AnnualTrend(data.annualTrend);

		data.checklist = Checklist.materialize(data.checklist);
	}

	static materialize(data, context) {
		context.setFiscalYear(FiscalYear.materialize(data.fiscalYear, context));

		context.setExtraProperty(
			"chartOfAccounts",
			ChartOfAccounts.materialize(data.chartOfAccounts, context),
		);

		return super.materialize(data, context);
	}
}
